import { Head, Link } from "@inertiajs/react"
import { useEffect } from "react"
import logoLight from "@/assets/logos/logo-light.svg"
import { CRUCIAL_CONVERSATION_FIELDS } from "@/lib/crucial-conversation-fields"

const CATEGORY_LABELS = {
  professional: "Professional",
  personal: "Personal",
  spiritual: "Spiritual",
}

function formatDate(dateString) {
  if (!dateString) return ""
  const date = new Date(dateString)
  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  })
}

function PrintField({ label, value }) {
  if (!value) return null

  return (
    <section style={{ marginBottom: "1.25rem", pageBreakInside: "avoid" }}>
      <h2
        style={{
          fontSize: "0.85rem",
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          color: "#000",
          margin: "0 0 0.35rem 0",
          borderBottom: "1px solid #000",
          paddingBottom: "0.15rem",
        }}
      >
        {label}
      </h2>
      <p
        style={{
          fontSize: "1rem",
          lineHeight: 1.5,
          whiteSpace: "pre-wrap",
          margin: 0,
          color: "#000",
        }}
      >
        {value}
      </p>
    </section>
  )
}

export default function CrucialConversationPrint({ crucialConversation }) {
  const cc = crucialConversation

  useEffect(() => {
    // Slight delay to ensure styles + content render before invoking print
    const t = window.setTimeout(() => {
      window.print()
    }, 250)
    return () => window.clearTimeout(t)
  }, [])

  return (
    <>
      <Head title={`${cc.topicIssue} - Print - Kingdom Factor`} />
      <style>{`
        @media print {
          .kf-print-instructions { display: none !important; }
          body { background: #fff !important; }
          .kf-print-page { padding: 0 !important; max-width: none !important; }
          .kf-print-brand img { height: 36px !important; }
        }
        @page { margin: 0.75in; }
      `}</style>

      <main
        className="kf-print-page"
        style={{
          maxWidth: "7in",
          margin: "0 auto",
          padding: "1.5rem",
          background: "#fff",
          color: "#000",
          fontFamily: "Georgia, 'Times New Roman', serif",
        }}
      >
        <div
          className="kf-print-instructions"
          style={{
            background: "#fef3c7",
            border: "1px solid #f59e0b",
            borderRadius: "0.5rem",
            padding: "0.75rem 1rem",
            marginBottom: "1.5rem",
            fontFamily: "system-ui, -apple-system, sans-serif",
            fontSize: "0.9rem",
            color: "#78350f",
          }}
        >
          <p style={{ margin: 0 }}>
            Press <strong>Cmd/Ctrl + P</strong> or use your browser's print
            dialog to save as PDF. Your print dialog should have opened
            automatically.
          </p>
          <p style={{ margin: "0.5rem 0 0 0" }}>
            <Link
              href={`/app/member/crucial_conversations/${cc.id}`}
              style={{ color: "#78350f", textDecoration: "underline" }}
            >
              &larr; Back to Crucial Conversation
            </Link>
          </p>
        </div>

        <div
          className="kf-print-brand"
          style={{
            marginBottom: "1.25rem",
            pageBreakInside: "avoid",
            fontFamily: "system-ui, -apple-system, sans-serif",
          }}
        >
          <img
            src={logoLight}
            alt="Kingdom Factor"
            style={{
              height: "40px",
              width: "auto",
              display: "block",
            }}
          />
        </div>

        <header
          style={{
            borderBottom: "2px solid #000",
            paddingBottom: "0.75rem",
            marginBottom: "1.5rem",
          }}
        >
          <p
            style={{
              fontSize: "0.85rem",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.1em",
              margin: "0 0 0.35rem 0",
              color: "#000",
            }}
          >
            Crucial Conversation
          </p>
          <h1
            style={{
              fontSize: "1.6rem",
              fontWeight: 700,
              margin: "0 0 0.5rem 0",
              color: "#000",
              lineHeight: 1.2,
            }}
          >
            {cc.topicIssue}
          </h1>
          <dl
            style={{
              display: "grid",
              gridTemplateColumns: "max-content 1fr",
              gap: "0.15rem 0.75rem",
              margin: "0.5rem 0 0 0",
              fontSize: "0.9rem",
              color: "#000",
            }}
          >
            <dt style={{ fontWeight: 600 }}>Author:</dt>
            <dd style={{ margin: 0 }}>{cc.author?.name}</dd>
            <dt style={{ fontWeight: 600 }}>Created:</dt>
            <dd style={{ margin: 0 }}>{formatDate(cc.createdAt)}</dd>
            {cc.groupName && (
              <>
                <dt style={{ fontWeight: 600 }}>Group:</dt>
                <dd style={{ margin: 0 }}>{cc.groupName}</dd>
              </>
            )}
            {cc.category && (
              <>
                <dt style={{ fontWeight: 600 }}>Category:</dt>
                <dd style={{ margin: 0 }}>
                  {CATEGORY_LABELS[cc.category] || cc.category}
                </dd>
              </>
            )}
            {cc.urgency != null && (
              <>
                <dt style={{ fontWeight: 600 }}>Urgency:</dt>
                <dd style={{ margin: 0 }}>{cc.urgency}/10</dd>
              </>
            )}
          </dl>
        </header>

        {CRUCIAL_CONVERSATION_FIELDS.map((field) => (
          <PrintField key={field.name} label={field.label} value={cc[field.prop]} />
        ))}

        {cc.pdfFileName && (
          <section style={{ marginTop: "1.5rem", pageBreakInside: "avoid" }}>
            <h2
              style={{
                fontSize: "0.85rem",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                color: "#000",
                margin: "0 0 0.35rem 0",
              }}
            >
              Attachment
            </h2>
            <p style={{ fontSize: "0.95rem", margin: 0, color: "#000" }}>
              {cc.pdfFileName}
            </p>
          </section>
        )}
      </main>
    </>
  )
}
